import type { InchFraction } from './casingInches';
import {
  formatInchesStored,
  hydrateCasingInchUiFromStored,
  inchesPartsFilled,
  inchesPartsToDecimal
} from './casingInches';

export type RequestStatus =
  | 'draft'
  | 'pendingApproval'
  | 'procurementReview'
  | 'sourcing'
  | 'orderPlaced'
  | 'procured'
  | 'complete'
  | 'rejected';

export interface WellLocation {
  state: string;
  country: string;
}

export interface WellInfo {
  wellId: string;
  apiNumber?: string;
  wellName: string;
  field: string;
  /** Region code from the well master (matches region catalog). */
  region: string;
  location: WellLocation;
  depthFt?: number;
  reservoirType: string;
}

export interface RequestorProfile {
  userId: string;
  name: string;
  role: string;
  region: string;
}

/** Snapshot of the selected region catalog field (mirrors API RegionFieldInfo). */
export interface RegionFieldInfo {
  id: string;
  name: string;
  county: string;
  state: string;
  closestCity: string;
  milesFromCity?: number | null;
}

export type MaterialLineKind =
  | 'Tubular'
  | 'StringDesign'
  | 'Tubing'
  | 'Casing'
  | 'Rods'
  | 'Transformers'
  | 'ElectricMotors'
  | 'PumpingUnits'
  | 'LinePipe'
  | 'TanksPressureVesselsKnockOuts'
  | 'EspCables'
  | 'FlaresCombustors'
  | 'StorageTank'
  | 'PressureVessel'
  | 'KnockOut'
  | 'Flares'
  | 'Combustor';

export type StringDesignSubType = 'SurfaceCasing' | 'IntermediateCasing' | 'ProductionString';

export type RodMaterialFamily = 'Steel' | 'Fiberglass';

export type SteelSuckerRodApiGrade = 'C' | 'D' | 'K';

export interface RodLineDetails {
  materialFamily: RodMaterialFamily;
  steelGrade?: SteelSuckerRodApiGrade;
  rodDiameterInchesWhole: string;
  rodDiameterInchesFraction: InchFraction | '';
  lengthFeet?: number;
  ponyRodsLengthInches?: number;
  endConnectionsQuantity?: number;
  rodGuidesQuantity?: number;
  polishedRodLengthFeet?: number;
  polishedRodInchesWhole: string;
  polishedRodInchesFraction: InchFraction | '';
}

export type TransformerCooling = 'ONAN' | 'ONAF';
export type MotorEnclosure = 'TEFC' | 'ODP';
export type PumpingUnitType = 'BeamPumping' | 'HydraulicPumping';
export type CounterbalanceType = 'Counterbalanced' | 'AirBalanced';
export type PrimeMoverType = 'AcElectricMotor' | 'InternalCombustionEngine';
export type StorageTankStandard = 'ShopWeldedApi12F' | 'FieldErectedApi650';
export type KnockOutDrumType = 'FreeWaterKnockOut' | 'GasKnockOutDrum';

export interface TransformerLineDetails {
  voltageRating: string;
  capacity: string;
  cooling?: TransformerCooling;
  insulationClass: string;
  impedancePercent?: number;
  environmentalProtection: string;
  oilType: string;
}

export interface ElectricMotorLineDetails {
  powerOutputHp?: number;
  torqueDesign: string;
  speedRpm?: number;
  voltage?: number;
  enclosure?: MotorEnclosure;
  duty: string;
}

export interface PumpingUnitBeamSpec {
  apiSpecifications: string;
  loadRatingLbs?: number;
  strokeLengthFeet?: number;
  strokeInchesWhole: string;
  strokeInchesFraction: InchFraction | '';
  counterbalance?: CounterbalanceType;
  primeMover?: PrimeMoverType;
  operatingStrokesPerMinute?: number;
}

export interface PumpingUnitHydraulicSpec {
  strokeFeet?: number;
  strokeInchesWhole: string;
  strokeInchesFraction: InchFraction | '';
  peakLoadLbm?: number;
}

export interface PumpingUnitLineDetails {
  unitType: PumpingUnitType;
  beam?: PumpingUnitBeamSpec;
  hydraulic?: PumpingUnitHydraulicSpec;
}

export interface LinePipeLineDetails {
  diameter: string;
  wallThickness: string;
  lengthFeet?: number;
  lengthInchesWhole: string;
  lengthInchesFraction: InchFraction | '';
  endFinishes: string;
  manufacturingProcess: string;
  grade: string;
}

export interface StorageTankLineDetails {
  standard?: StorageTankStandard;
  capacityBbl?: number;
  operatingPressurePsig?: number;
  materials: string;
}

export interface PressureVesselLineDetails {
  standards: string;
  pressureRatingPsig?: number;
}

export interface KnockOutLineDetails {
  drumType?: KnockOutDrumType;
  retentionTimeMinutes?: number;
  sizes: string;
}

export interface EspCableLineDetails {
  voltageKv?: number;
  temperatureFahrenheit?: number;
  conductorSizes: string;
  cableGeometry: string;
  conductors: string;
  insulation: string;
  barrierOrSheath: string;
  jacket: string;
  armor: string;
}

export interface FlareLineDetails {
  standards: string;
  destructionEfficiencyPercent?: number;
  netHeatingValueBtuScf?: number;
  tipVelocityFps?: number;
  visibleEmissions: string;
  pilot?: boolean;
}

export interface CombustorLineDetails {
  performancePercent?: number;
  flowLimits: string;
}

/** API serializes as "new" | "used" (JsonStringEnumConverter). */
export type MaterialCondition = 'New' | 'Used';

/** Stored on each material line; multi-select on the form. */
export type InspectionTypeCode = 'None' | 'MillOnly' | 'Emi' | 'Other' | 'VtiSea' | 'Fld';

const INSPECTION_LABELS: Record<InspectionTypeCode, string> = {
  None: 'NONE',
  MillOnly: 'MILL ONLY',
  Emi: 'EMI',
  Other: 'OTHER',
  VtiSea: 'VTI/SEA',
  Fld: 'FLD'
};

export function inspectionTypeLabel(code: string): string {
  return INSPECTION_LABELS[code as InspectionTypeCode] ?? code;
}

const INSPECTION_CODES_LIST: InspectionTypeCode[] = ['None', 'MillOnly', 'Emi', 'Other', 'VtiSea', 'Fld'];

function parseInspectionTypeCode(raw: string): InspectionTypeCode | null {
  const t = raw.trim();
  if (!t) {
    return null;
  }
  const found = INSPECTION_CODES_LIST.find((c) => c.toLowerCase() === t.toLowerCase());
  return found ?? null;
}

const MATERIAL_KIND_FROM_API: Record<string, MaterialLineKind> = {
  tubular: 'Tubular',
  stringdesign: 'StringDesign',
  tubing: 'Tubing',
  casing: 'Casing',
  rods: 'Rods',
  transformers: 'Transformers',
  electricmotors: 'ElectricMotors',
  pumpingunits: 'PumpingUnits',
  linepipe: 'LinePipe',
  tankspressurevesselsknockouts: 'TanksPressureVesselsKnockOuts',
  espcables: 'EspCables',
  flarescombustors: 'FlaresCombustors',
  storagetank: 'StorageTank',
  pressurevessel: 'PressureVessel',
  knockout: 'KnockOut',
  flares: 'Flares',
  combustor: 'Combustor'
};

/** API uses JsonStringEnumConverter(JsonNamingPolicy.CamelCase) — e.g. stringDesign, tanksPressureVesselsKnockOuts. */
function parseMaterialLineKind(raw: unknown): MaterialLineKind {
  const s = String(raw ?? '').trim();
  if (!s) {
    return 'Tubular';
  }
  return MATERIAL_KIND_FROM_API[s.toLowerCase()] ?? 'Tubular';
}

/**
 * UI label for API material line kind (e.g. inventory list): same mapping as requests so "tubular" shows as "Tubular".
 * Unknown or empty values get a light fallback, not a default to Tubular.
 */
export function displayMaterialLineKindFromApi(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (!s) {
    return '—';
  }
  const fromMap = MATERIAL_KIND_FROM_API[s.toLowerCase()];
  if (fromMap) {
    return fromMap;
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function usesCasingMaterialForm(kind: MaterialLineKind): boolean {
  return kind === 'Tubular' || kind === 'StringDesign' || kind === 'Tubing' || kind === 'Casing';
}

export function isGenericMaterialKindUi(kind: MaterialLineKind): boolean {
  return kind === 'TanksPressureVesselsKnockOuts' || kind === 'FlaresCombustors';
}

export function usesStructuredEquipmentForm(kind: MaterialLineKind): boolean {
  return (
    kind === 'LinePipe' ||
    kind === 'Transformers' ||
    kind === 'ElectricMotors' ||
    kind === 'PumpingUnits' ||
    kind === 'EspCables' ||
    kind === 'StorageTank' ||
    kind === 'PressureVessel' ||
    kind === 'KnockOut' ||
    kind === 'Flares' ||
    kind === 'Combustor'
  );
}

/** Condition / testing / inspection block hidden for these kinds (per product rules). */
export function skipsMaterialLogisticsSection(kind: MaterialLineKind): boolean {
  return kind === 'ElectricMotors' || kind === 'PumpingUnits';
}

function parseStringDesignSubType(raw: unknown): StringDesignSubType | undefined {
  const s = String(raw ?? '').trim();
  if (!s) {
    return undefined;
  }
  const lower = s.toLowerCase();
  if (lower === 'surfacecasing') {
    return 'SurfaceCasing';
  }
  if (lower === 'intermediatecasing') {
    return 'IntermediateCasing';
  }
  if (lower === 'productionstring') {
    return 'ProductionString';
  }
  return undefined;
}

const STRING_DESIGN_SUBTYPE_LABELS: Record<StringDesignSubType, string> = {
  SurfaceCasing: 'Surface casing',
  IntermediateCasing: 'Intermediate casing',
  ProductionString: 'Production string'
};

/** Read-only one-line summary for request detail / PDF helpers. */
export function formatMaterialLogisticsLine(m: MaterialItem): string {
  const parts: string[] = [];
  if (m.condition) {
    parts.push(m.condition === 'New' ? 'New' : 'Used');
  }
  if (m.condition === 'Used' && m.bandIfUsed?.trim()) {
    parts.push(`BAND ${m.bandIfUsed.trim()}`);
  }
  if (m.tested !== undefined) {
    parts.push(`Tested: ${m.tested ? 'Yes' : 'No'}`);
  }
  if (m.tested === true && m.pressureNumber?.trim()) {
    parts.push(`Pressure: ${m.pressureNumber.trim()}`);
  }
  const ins = m.inspectionTypesRequired ?? [];
  if (ins.length > 0) {
    parts.push(`Inspection: ${ins.map((c) => inspectionTypeLabel(c)).join(', ')}`);
  }
  if (m.fullTruckload !== undefined) {
    parts.push(`Full truckload: ${m.fullTruckload ? 'Yes' : 'No'}`);
  }
  return parts.join(' · ');
}

/** Read-only label/value pairs for request detail (full material line). */
export interface MaterialLineDetailRow {
  label: string;
  value: string;
}

function fmtOptionalDecimal(n: number | undefined): string {
  if (n === undefined || Number.isNaN(Number(n))) {
    return '—';
  }
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 3 });
}

function fmtMoneyUsd(n: number | undefined): string {
  if (n === undefined || Number.isNaN(Number(n))) {
    return '—';
  }
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
}

/** All persisted fields on a material line for detail panel / parity with editor. */
export function materialLineDetailRows(m: MaterialItem): MaterialLineDetailRow[] {
  const rows: MaterialLineDetailRow[] = [];
  rows.push({ label: 'Item id', value: m.itemId?.trim() || '—' });
  if (m.sku?.trim()) {
    rows.push({ label: 'SKU', value: m.sku.trim() });
  }
  rows.push({ label: 'Kind', value: m.kind });
  if (m.kind === 'StringDesign' && m.stringDesignSubType) {
    rows.push({
      label: 'String segment',
      value: STRING_DESIGN_SUBTYPE_LABELS[m.stringDesignSubType]
    });
  }
  rows.push({ label: 'Name', value: m.name?.trim() || '—' });
  rows.push({ label: 'Description', value: m.description?.trim() || '—' });
  rows.push({
    label: 'Quantity',
    value: typeof m.quantity === 'number' && Number.isFinite(m.quantity) ? String(m.quantity) : '—'
  });
  rows.push({ label: 'Unit', value: m.unit?.trim() || '—' });

  const c = m.casing;
  if (usesCasingMaterialForm(m.kind) && c) {
    rows.push({ label: 'Net footage (ft)', value: fmtOptionalDecimal(c.netFootage) });
    rows.push({ label: 'Plus makeup / thread loss (in)', value: fmtOptionalDecimal(c.plusMakeupThreadLoss) });
    rows.push({ label: 'Gross footage (ft)', value: fmtOptionalDecimal(c.grossFootage) });
    rows.push({ label: 'Size / OD', value: c.sizeDiameter?.trim() || '—' });
    rows.push({ label: 'Weight (lb/ft)', value: c.weight?.trim() || '—' });
    rows.push({ label: 'Grade', value: c.grade?.trim() || '—' });
    rows.push({ label: 'Thread', value: c.thread?.trim() || '—' });
    const weld =
      c.mfgProcess === 'Erw' ? 'ERW' : c.mfgProcess === 'Seamless' ? 'Seamless' : '—';
    rows.push({ label: 'Mfg. process', value: weld });
  }

  if (m.kind === 'Rods' && m.rods) {
    const r = m.rods;
    rows.push({ label: 'Rod material', value: r.materialFamily });
    if (r.materialFamily === 'Steel' && r.steelGrade) {
      rows.push({ label: 'API steel grade', value: r.steelGrade });
    }
    rows.push({
      label: 'Rod diameter (in.)',
      value: formatInchesStored(r.rodDiameterInchesWhole, r.rodDiameterInchesFraction) || '—'
    });
    rows.push({ label: 'Length (ft)', value: fmtOptionalDecimal(r.lengthFeet) });
    rows.push({ label: 'Pony rods (in.)', value: fmtOptionalDecimal(r.ponyRodsLengthInches) });
    rows.push({
      label: 'End connections (qty)',
      value: r.endConnectionsQuantity != null ? String(r.endConnectionsQuantity) : '—'
    });
    rows.push({
      label: 'Rod guides (qty)',
      value: r.rodGuidesQuantity != null ? String(r.rodGuidesQuantity) : '—'
    });
    const prParts: string[] = [];
    if (r.polishedRodLengthFeet != null) {
      prParts.push(`${fmtOptionalDecimal(r.polishedRodLengthFeet)} ft`);
    }
    const prIn = formatInchesStored(r.polishedRodInchesWhole, r.polishedRodInchesFraction);
    if (prIn) {
      prParts.push(`${prIn} in`);
    }
    rows.push({ label: 'Polished rod length', value: prParts.length > 0 ? prParts.join(' + ') : '—' });
  }

  const specKeys = Object.keys(m.specifications ?? {}).sort();
  for (const k of specKeys) {
    rows.push({ label: `Specification (${k})`, value: (m.specifications ?? {})[k]?.trim() || '—' });
  }

  rows.push({ label: 'Est. unit cost', value: fmtMoneyUsd(m.estimatedUnitCost) });
  rows.push({ label: 'Total est. cost', value: fmtMoneyUsd(m.totalEstimatedCost) });

  if (m.condition) {
    rows.push({ label: 'Condition', value: m.condition });
  }
  if (m.condition === 'Used' && m.bandIfUsed?.trim()) {
    rows.push({ label: 'Band (if used)', value: m.bandIfUsed.trim() });
  }
  if (m.tested !== undefined) {
    rows.push({ label: 'Pressure tested', value: m.tested ? 'Yes' : 'No' });
  }
  if (m.tested === true && m.pressureNumber?.trim()) {
    rows.push({ label: 'Pressure', value: m.pressureNumber.trim() });
  }
  const ins = m.inspectionTypesRequired ?? [];
  if (ins.length > 0) {
    rows.push({
      label: 'Inspection required',
      value: ins.map((code) => inspectionTypeLabel(code)).join(', ')
    });
  }
  if (m.fullTruckload !== undefined) {
    rows.push({ label: 'Full truckload', value: m.fullTruckload ? 'Yes' : 'No' });
  }
  return rows;
}

function parseMaterialCondition(raw: unknown): MaterialCondition | undefined {
  const s = String(raw ?? '').toLowerCase();
  if (s === 'new' || s === 'used') {
    return s === 'new' ? 'New' : 'Used';
  }
  return undefined;
}

export interface CasingLineDetails {
  netFootage?: number;
  plusMakeupThreadLoss?: number;
  grossFootage?: number;
  /** Persisted combined string (e.g. <c>4 1/2"</c>); kept in sync with inch fields on save. */
  sizeDiameter: string;
  /** UI: whole inches for diameter (user-entered). */
  sizeInchesWhole?: string;
  /** UI: fractional inches for diameter. */
  sizeInchesFraction?: InchFraction;
  /** UI: whole inches for makeup / thread loss. */
  plusMakeupInchesWhole?: string;
  /** UI: fractional inches for makeup / thread loss. */
  plusMakeupInchesFraction?: InchFraction;
  /** Weight (lb), decimal as string for API. */
  weight: string;
  grade: string;
  thread: string;
  /** Manufacturing process (ERW vs seamless). Serialized to API as `erwOrSeamless`. */
  mfgProcess: '' | 'Erw' | 'Seamless';
}

/** Default required length (ft) for vendor RFQ tubular/string-design lines (matches API heuristic). */
export function defaultRequiredQtyFtForQuote(m: MaterialItem): number {
  if (m.kind === 'Rods' && m.rods?.lengthFeet != null && m.rods.lengthFeet > 0) {
    return m.rods.lengthFeet;
  }
  const g = m.casing?.grossFootage;
  if (g != null && g > 0) {
    return g;
  }
  const n = m.casing?.netFootage;
  if (n != null && n > 0) {
    return n;
  }
  const u = (m.unit ?? '').trim();
  if (
    m.quantity > 0 &&
    (/\bft\b/i.test(u) || /foot/i.test(u) || /^LF$/i.test(u))
  ) {
    return m.quantity;
  }
  return m.quantity > 0 ? m.quantity : 0;
}

const FOOTAGE_KINDS: MaterialLineKind[] = [
  'Tubular',
  'StringDesign',
  'Tubing',
  'Casing',
  'Rods',
  'LinePipe',
  'EspCables'
];

export function materialUsesFootage(kind: MaterialLineKind): boolean {
  return FOOTAGE_KINDS.includes(kind);
}

export function materialInventoryNeed(m: MaterialItem): {
  trackFootage: boolean;
  requested: number;
  fulfilled: number;
  remaining: number;
  unit: string;
} {
  if (materialUsesFootage(m.kind)) {
    const requested = defaultRequiredQtyFtForQuote(m);
    const fulfilled = Number(m.inventoryFulfilledFootage ?? 0);
    return {
      trackFootage: true,
      requested,
      fulfilled,
      remaining: Math.max(0, requested - fulfilled),
      unit: 'ft'
    };
  }
  const requested = Number(m.quantity ?? 0);
  const fulfilled = Number(m.inventoryFulfilledQuantity ?? 0);
  return {
    trackFootage: false,
    requested,
    fulfilled,
    remaining: Math.max(0, requested - fulfilled),
    unit: (m.unit ?? '').trim() || 'qty'
  };
}

export interface MaterialItem {
  itemId: string;
  kind: MaterialLineKind;
  stringDesignSubType?: StringDesignSubType;
  casing?: CasingLineDetails;
  rods?: RodLineDetails;
  transformer?: TransformerLineDetails;
  electricMotor?: ElectricMotorLineDetails;
  pumpingUnit?: PumpingUnitLineDetails;
  linePipe?: LinePipeLineDetails;
  storageTank?: StorageTankLineDetails;
  pressureVessel?: PressureVesselLineDetails;
  knockOut?: KnockOutLineDetails;
  espCable?: EspCableLineDetails;
  flare?: FlareLineDetails;
  combustor?: CombustorLineDetails;
  name: string;
  description: string;
  quantity: number;
  unit: string;
  specifications: Record<string, string>;
  estimatedUnitCost?: number;
  totalEstimatedCost?: number;
  condition?: MaterialCondition;
  bandIfUsed?: string;
  tested?: boolean;
  pressureNumber?: string;
  /** Multi-select inspection types; empty until user selects (submit requires at least one). */
  inspectionTypesRequired?: InspectionTypeCode[];
  fullTruckload?: boolean;
  /** Material stock-keeping identifier (from admin SKU pattern or manual). */
  sku?: string;
  /** When true, spec-driven auto SKU does not overwrite `sku` on the client. */
  skuIsCustom?: boolean;
  /** Quantity already moved from yard stock toward this line. */
  inventoryFulfilledQuantity?: number;
  /** Footage already moved from yard stock toward this line. */
  inventoryFulfilledFootage?: number;
}

export interface RequestTimeline {
  requiredByDate?: string;
  expectedDeliveryDate?: string;
}

export interface PreferredVendorRef {
  vendorId: string;
  name: string;
}

export interface VendorPreferencesSection {
  preferredVendors: PreferredVendorRef[];
  allowAlternateVendors: boolean;
}

export interface ComplianceSection {
  safetyStandards: string[];
  environmentalRegulations: string[];
}

export interface RequestAttachment {
  fileName: string;
  fileType: string;
  url: string;
}

export interface RequestCollaborationNote {
  id: string;
  authorUserId: string;
  authorDisplayName: string;
  body: string;
  createdAtUtc: string;
}

/** Status changes and workflow actions from the API (activityLog). */
export interface RequestActivityEntry {
  atUtc: string;
  actorDisplayName: string;
  actorUsername?: string;
  message: string;
}

function normalizeActivityLog(raw: unknown): RequestActivityEntry[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((x) => {
    const o = x as Record<string, unknown>;
    const at = o.atUtc;
    const atUtc =
      typeof at === 'string'
        ? at
        : at instanceof Date
          ? at.toISOString()
          : String(at ?? '');
    return {
      atUtc,
      actorDisplayName: String(o.actorDisplayName ?? ''),
      actorUsername: o.actorUsername != null ? String(o.actorUsername) : undefined,
      message: String(o.message ?? '')
    };
  });
}

function normalizeCollaborationNote(raw: Record<string, unknown>): RequestCollaborationNote {
  return {
    id: String(raw.id ?? ''),
    authorUserId: String(raw.authorUserId ?? ''),
    authorDisplayName: String(raw.authorDisplayName ?? ''),
    body: String(raw.body ?? ''),
    createdAtUtc: String(raw.createdAtUtc ?? '')
  };
}

export function normalizeCollaborationNotesList(raw: unknown): RequestCollaborationNote[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((x) => normalizeCollaborationNote(x as Record<string, unknown>));
}

export interface RequestAudit {
  createdBy: string;
  createdOn: string;
  lastModifiedBy: string;
  lastModifiedOn: string;
}

/** API entity (camelCase from ASP.NET). */
export interface ProcurementRequest {
  id: string;
  requestId?: string;
  requestType: string;
  requestor: RequestorProfile;
  wellInfo: WellInfo;
  afeNumber?: string;
  regionFieldInfo?: RegionFieldInfo;
  deliveryAddressId?: string;
  businessJustification: string;
  materials: MaterialItem[];
  timeline?: RequestTimeline;
  vendorPreferences?: VendorPreferencesSection;
  compliance?: ComplianceSection;
  attachments?: RequestAttachment[];
  audit: RequestAudit;
  status: RequestStatus;
  regionalManagerName?: string;
  regionalDecisionComment?: string;
  procurementManagerName?: string;
  orderPlacedAtUtc?: string;
  procuredAtUtc?: string;
  fulfilledByMaterialTransferId?: string;
  fulfilledByMaterialTransferNumber?: string;
  /** Set when sourcing started because a linked material-transfer draft was created. */
  sourcingInitiatedByMaterialTransferId?: string;
  createdAtUtc: string;
  updatedAtUtc?: string;
  collaborationNotes?: RequestCollaborationNote[];
  activityLog?: RequestActivityEntry[];
}

/** Form / API POST body shape (matches CreateProcurementRequestDto). */
export interface ProcurementRequestFormState {
  requestType: string;
  requestor: RequestorProfile;
  wellInfo: WellInfo;
  afeNumber: string;
  regionFieldInfo: RegionFieldInfo;
  deliveryAddressId: string;
  businessJustification: string;
  materials: MaterialItem[];
  timeline: RequestTimeline;
  vendorPreferences: VendorPreferencesSection;
  compliance: ComplianceSection;
  attachments: RequestAttachment[];
}

export const emptyWellLocation = (): WellLocation => ({ state: '', country: '' });

export const emptyWellInfo = (): WellInfo => ({
  wellId: '',
  apiNumber: '',
  wellName: '',
  field: '',
  region: '',
  location: emptyWellLocation(),
  reservoirType: ''
});

export const emptyRegionFieldInfo = (): RegionFieldInfo => ({
  id: '',
  name: '',
  county: '',
  state: '',
  closestCity: '',
  milesFromCity: undefined
});

export const emptyCasing = (): CasingLineDetails => ({
  sizeDiameter: '',
  sizeInchesWhole: '',
  sizeInchesFraction: '',
  plusMakeupInchesWhole: '',
  plusMakeupInchesFraction: '',
  weight: '',
  grade: '',
  thread: '',
  mfgProcess: ''
});

export const emptyRods = (): RodLineDetails => ({
  materialFamily: 'Steel',
  steelGrade: 'C',
  rodDiameterInchesWhole: '',
  rodDiameterInchesFraction: '',
  polishedRodInchesWhole: '',
  polishedRodInchesFraction: ''
});

export const emptyTransformer = (): TransformerLineDetails => ({
  voltageRating: '',
  capacity: '',
  insulationClass: '',
  environmentalProtection: '',
  oilType: ''
});

export const emptyElectricMotor = (): ElectricMotorLineDetails => ({
  torqueDesign: '',
  duty: ''
});

export const emptyPumpingUnitBeam = (): PumpingUnitBeamSpec => ({
  apiSpecifications: '',
  strokeInchesWhole: '',
  strokeInchesFraction: ''
});

export const emptyPumpingUnitHydraulic = (): PumpingUnitHydraulicSpec => ({
  strokeInchesWhole: '',
  strokeInchesFraction: ''
});

export const emptyPumpingUnit = (): PumpingUnitLineDetails => ({
  unitType: 'BeamPumping',
  beam: emptyPumpingUnitBeam(),
  hydraulic: emptyPumpingUnitHydraulic()
});

export const emptyLinePipe = (): LinePipeLineDetails => ({
  diameter: '',
  wallThickness: '',
  lengthInchesWhole: '',
  lengthInchesFraction: '',
  endFinishes: '',
  manufacturingProcess: '',
  grade: ''
});

export const emptyStorageTank = (): StorageTankLineDetails => ({
  materials: ''
});

export const emptyPressureVessel = (): PressureVesselLineDetails => ({
  standards: ''
});

export const emptyKnockOut = (): KnockOutLineDetails => ({
  sizes: ''
});

export const emptyEspCable = (): EspCableLineDetails => ({
  conductorSizes: '',
  cableGeometry: '',
  conductors: '',
  insulation: '',
  barrierOrSheath: '',
  jacket: '',
  armor: ''
});

export const emptyFlare = (): FlareLineDetails => ({
  standards: '',
  visibleEmissions: ''
});

export const emptyCombustor = (): CombustorLineDetails => ({
  flowLimits: ''
});

export const emptyMaterial = (): MaterialItem => ({
  itemId: '',
  kind: 'Tubular',
  name: 'Tubular',
  description: '',
  quantity: 1,
  unit: '',
  specifications: {},
  estimatedUnitCost: undefined,
  totalEstimatedCost: undefined,
  casing: emptyCasing(),
  condition: 'New',
  bandIfUsed: '',
  tested: false,
  pressureNumber: '',
  inspectionTypesRequired: [],
  fullTruckload: false,
  sku: '',
  skuIsCustom: false
});

export const emptyProcurementForm = (): ProcurementRequestFormState => ({
  requestType: 'Material',
  requestor: { userId: '', name: '', role: '', region: '' },
  wellInfo: emptyWellInfo(),
  afeNumber: '',
  regionFieldInfo: emptyRegionFieldInfo(),
  deliveryAddressId: '',
  businessJustification: '',
  materials: [emptyMaterial()],
  timeline: {},
  vendorPreferences: { preferredVendors: [], allowAlternateVendors: true },
  compliance: { safetyStandards: [], environmentalRegulations: [] },
  attachments: []
});

export function requestorFormWithUser(displayName: string, username: string): ProcurementRequestFormState {
  const f = emptyProcurementForm();
  f.requestor.name = displayName;
  f.requestor.userId = username;
  return f;
}

function emptyAudit(): RequestAudit {
  const t = new Date(0).toISOString();
  return { createdBy: '', createdOn: t, lastModifiedBy: '', lastModifiedOn: t };
}

function parseTransformerFromApi(o: Record<string, unknown> | undefined): TransformerLineDetails | undefined {
  if (!o) {
    return undefined;
  }
  const c = String(o.cooling ?? '');
  const cooling: TransformerCooling | undefined = /^onaf$/i.test(c) ? 'ONAF' : /^onan$/i.test(c) ? 'ONAN' : undefined;
  return {
    ...emptyTransformer(),
    voltageRating: String(o.voltageRating ?? ''),
    capacity: String(o.capacity ?? ''),
    cooling,
    insulationClass: String(o.insulationClass ?? ''),
    impedancePercent: o.impedancePercent != null && o.impedancePercent !== '' ? Number(o.impedancePercent) : undefined,
    environmentalProtection: String(o.environmentalProtection ?? ''),
    oilType: String(o.oilType ?? '')
  };
}

function parseElectricMotorFromApi(o: Record<string, unknown> | undefined): ElectricMotorLineDetails | undefined {
  if (!o) {
    return undefined;
  }
  const enc = String(o.enclosure ?? '');
  const enclosure: MotorEnclosure | undefined = /^odp$/i.test(enc) ? 'ODP' : /^tefc$/i.test(enc) ? 'TEFC' : undefined;
  return {
    ...emptyElectricMotor(),
    powerOutputHp: o.powerOutputHp != null ? Number(o.powerOutputHp) : undefined,
    torqueDesign: String(o.torqueDesign ?? ''),
    speedRpm: o.speedRpm != null ? Number(o.speedRpm) : undefined,
    voltage: o.voltage != null ? Number(o.voltage) : undefined,
    enclosure,
    duty: String(o.duty ?? '')
  };
}

function parsePumpingUnitFromApi(o: Record<string, unknown> | undefined): PumpingUnitLineDetails | undefined {
  if (!o) {
    return undefined;
  }
  const ut = String(o.unitType ?? 'BeamPumping');
  const uLower = ut.toLowerCase();
  const unitType: PumpingUnitType = uLower.includes('hydraulic') ? 'HydraulicPumping' : 'BeamPumping';
  const beamRaw = o.beam as Record<string, unknown> | undefined;
  const hyRaw = o.hydraulic as Record<string, unknown> | undefined;
  const bCounter = String(beamRaw?.counterbalance ?? '');
  const counterbalance: CounterbalanceType | undefined = /air/i.test(bCounter) ? 'AirBalanced' : /counter/i.test(bCounter) ? 'Counterbalanced' : undefined;
  const bPrime = String(beamRaw?.primeMover ?? '');
  const primeMover: PrimeMoverType | undefined = /combust|ice|internal/i.test(bPrime)
    ? 'InternalCombustionEngine'
    : /ac|electric|motor/i.test(bPrime)
      ? 'AcElectricMotor'
      : undefined;
  return {
    unitType,
    beam: beamRaw
      ? {
          ...emptyPumpingUnitBeam(),
          apiSpecifications: String(beamRaw.apiSpecifications ?? ''),
          loadRatingLbs: beamRaw.loadRatingLbs != null ? Number(beamRaw.loadRatingLbs) : undefined,
          strokeLengthFeet: beamRaw.strokeLengthFeet != null ? Number(beamRaw.strokeLengthFeet) : undefined,
          strokeInchesWhole: String(beamRaw.strokeInchesWhole ?? ''),
          strokeInchesFraction: String(beamRaw.strokeInchesFraction ?? '') as InchFraction | '',
          counterbalance,
          primeMover,
          operatingStrokesPerMinute:
            beamRaw.operatingStrokesPerMinute != null ? Number(beamRaw.operatingStrokesPerMinute) : undefined
        }
      : emptyPumpingUnitBeam(),
    hydraulic: hyRaw
      ? {
          ...emptyPumpingUnitHydraulic(),
          strokeFeet: hyRaw.strokeFeet != null ? Number(hyRaw.strokeFeet) : undefined,
          strokeInchesWhole: String(hyRaw.strokeInchesWhole ?? ''),
          strokeInchesFraction: String(hyRaw.strokeInchesFraction ?? '') as InchFraction | '',
          peakLoadLbm: hyRaw.peakLoadLbm != null ? Number(hyRaw.peakLoadLbm) : undefined
        }
      : emptyPumpingUnitHydraulic()
  };
}

function parseLinePipeFromApi(o: Record<string, unknown> | undefined): LinePipeLineDetails | undefined {
  if (!o) {
    return undefined;
  }
  return {
    ...emptyLinePipe(),
    diameter: String(o.diameter ?? ''),
    wallThickness: String(o.wallThickness ?? ''),
    lengthFeet: o.lengthFeet != null ? Number(o.lengthFeet) : undefined,
    lengthInchesWhole: String(o.lengthInchesWhole ?? ''),
    lengthInchesFraction: String(o.lengthInchesFraction ?? '') as InchFraction | '',
    endFinishes: String(o.endFinishes ?? ''),
    manufacturingProcess: String(o.manufacturingProcess ?? ''),
    grade: String(o.grade ?? '')
  };
}

function parseStorageTankFromApi(o: Record<string, unknown> | undefined): StorageTankLineDetails | undefined {
  if (!o) {
    return undefined;
  }
  const std = String(o.standard ?? '');
  const low = std.toLowerCase();
  let standard: StorageTankStandard | undefined;
  if (std === 'FieldErectedApi650' || low === 'fielderectedapi650' || (low.includes('field') && low.includes('650'))) {
    standard = 'FieldErectedApi650';
  } else if (std === 'ShopWeldedApi12F' || low === 'shopweldedapi12f' || (low.includes('12f') && low.includes('shop'))) {
    standard = 'ShopWeldedApi12F';
  }
  return {
    ...emptyStorageTank(),
    standard,
    capacityBbl: o.capacityBbl != null ? Number(o.capacityBbl) : undefined,
    operatingPressurePsig: o.operatingPressurePsig != null ? Number(o.operatingPressurePsig) : undefined,
    materials: String(o.materials ?? '')
  };
}

function parsePressureVesselFromApi(o: Record<string, unknown> | undefined): PressureVesselLineDetails | undefined {
  if (!o) {
    return undefined;
  }
  return {
    ...emptyPressureVessel(),
    standards: String(o.standards ?? ''),
    pressureRatingPsig: o.pressureRatingPsig != null ? Number(o.pressureRatingPsig) : undefined
  };
}

function parseKnockOutFromApi(o: Record<string, unknown> | undefined): KnockOutLineDetails | undefined {
  if (!o) {
    return undefined;
  }
  const d = String(o.drumType ?? '');
  let drumType: KnockOutDrumType | undefined;
  if (/gas/i.test(d)) {
    drumType = 'GasKnockOutDrum';
  } else if (/free|water|fwko/i.test(d)) {
    drumType = 'FreeWaterKnockOut';
  }
  return {
    ...emptyKnockOut(),
    drumType,
    retentionTimeMinutes: o.retentionTimeMinutes != null ? Number(o.retentionTimeMinutes) : undefined,
    sizes: String(o.sizes ?? '')
  };
}

function parseEspCableFromApi(o: Record<string, unknown> | undefined): EspCableLineDetails | undefined {
  if (!o) {
    return undefined;
  }
  return {
    ...emptyEspCable(),
    voltageKv: o.voltageKv != null ? Number(o.voltageKv) : undefined,
    temperatureFahrenheit: o.temperatureFahrenheit != null ? Number(o.temperatureFahrenheit) : undefined,
    conductorSizes: String(o.conductorSizes ?? ''),
    cableGeometry: String(o.cableGeometry ?? ''),
    conductors: String(o.conductors ?? ''),
    insulation: String(o.insulation ?? ''),
    barrierOrSheath: String(o.barrierOrSheath ?? ''),
    jacket: String(o.jacket ?? ''),
    armor: String(o.armor ?? '')
  };
}

function parseFlareFromApi(o: Record<string, unknown> | undefined): FlareLineDetails | undefined {
  if (!o) {
    return undefined;
  }
  return {
    ...emptyFlare(),
    standards: String(o.standards ?? ''),
    destructionEfficiencyPercent:
      o.destructionEfficiencyPercent != null ? Number(o.destructionEfficiencyPercent) : undefined,
    netHeatingValueBtuScf: o.netHeatingValueBtuScf != null ? Number(o.netHeatingValueBtuScf) : undefined,
    tipVelocityFps: o.tipVelocityFps != null ? Number(o.tipVelocityFps) : undefined,
    visibleEmissions: String(o.visibleEmissions ?? ''),
    pilot: o.pilot === undefined || o.pilot === null ? undefined : Boolean(o.pilot)
  };
}

function parseCombustorFromApi(o: Record<string, unknown> | undefined): CombustorLineDetails | undefined {
  if (!o) {
    return undefined;
  }
  return {
    ...emptyCombustor(),
    performancePercent: o.performancePercent != null ? Number(o.performancePercent) : undefined,
    flowLimits: String(o.flowLimits ?? '')
  };
}

function normalizeMaterialLine(raw: Record<string, unknown>): MaterialItem {
  const kind = parseMaterialLineKind(raw.kind);
  const stringDesignSubType = parseStringDesignSubType(raw.stringDesignSubType);
  const cr = raw.casing as Record<string, unknown> | undefined;
  let casing: CasingLineDetails | undefined;
  if (cr && typeof cr === 'object') {
    const mfgRaw = String(cr.erwOrSeamless ?? (cr as Record<string, unknown>).mfgProcess ?? '');
    const mfgNorm: '' | 'Erw' | 'Seamless' =
      mfgRaw.toLowerCase() === 'erw' ? 'Erw' : mfgRaw.toLowerCase() === 'seamless' ? 'Seamless' : '';
    casing = {
      netFootage: cr.netFootage !== undefined && cr.netFootage !== null ? Number(cr.netFootage) : undefined,
      plusMakeupThreadLoss:
        cr.plusMakeupThreadLoss !== undefined && cr.plusMakeupThreadLoss !== null
          ? Number(cr.plusMakeupThreadLoss)
          : undefined,
      grossFootage: cr.grossFootage !== undefined && cr.grossFootage !== null ? Number(cr.grossFootage) : undefined,
      sizeDiameter: String(cr.sizeDiameter ?? ''),
      weight: String(cr.weight ?? ''),
      grade: String(cr.grade ?? ''),
      thread: String(cr.thread ?? ''),
      mfgProcess: mfgNorm
    };
    hydrateCasingInchUiFromStored(casing);
  }
  const rr = raw.rods as Record<string, unknown> | undefined;
  let rods: RodLineDetails | undefined;
  if (rr && typeof rr === 'object') {
    const fam = String(rr.materialFamily ?? 'Steel').toLowerCase();
    const matFam: RodMaterialFamily = fam === 'fiberglass' ? 'Fiberglass' : 'Steel';
    const sgRaw = String(rr.steelGrade ?? '').toLowerCase();
    const steelGrade: SteelSuckerRodApiGrade | undefined =
      sgRaw === 'c' || sgRaw === 'd' || sgRaw === 'k' ? (sgRaw.toUpperCase() as SteelSuckerRodApiGrade) : undefined;
    rods = {
      materialFamily: matFam,
      steelGrade: matFam === 'Steel' ? steelGrade : undefined,
      rodDiameterInchesWhole: String(rr.rodDiameterInchesWhole ?? ''),
      rodDiameterInchesFraction: String(rr.rodDiameterInchesFraction ?? '') as InchFraction | '',
      lengthFeet:
        rr.lengthFeet !== undefined && rr.lengthFeet !== null ? Number(rr.lengthFeet) : undefined,
      ponyRodsLengthInches:
        rr.ponyRodsLengthInches !== undefined && rr.ponyRodsLengthInches !== null
          ? Number(rr.ponyRodsLengthInches)
          : undefined,
      endConnectionsQuantity:
        rr.endConnectionsQuantity !== undefined && rr.endConnectionsQuantity !== null
          ? Number(rr.endConnectionsQuantity)
          : undefined,
      rodGuidesQuantity:
        rr.rodGuidesQuantity !== undefined && rr.rodGuidesQuantity !== null
          ? Number(rr.rodGuidesQuantity)
          : undefined,
      polishedRodLengthFeet:
        rr.polishedRodLengthFeet !== undefined && rr.polishedRodLengthFeet !== null
          ? Number(rr.polishedRodLengthFeet)
          : undefined,
      polishedRodInchesWhole: String(rr.polishedRodInchesWhole ?? ''),
      polishedRodInchesFraction: String(rr.polishedRodInchesFraction ?? '') as InchFraction | ''
    };
  }

  const transformer = parseTransformerFromApi(raw.transformer as Record<string, unknown> | undefined);
  const electricMotor = parseElectricMotorFromApi(raw.electricMotor as Record<string, unknown> | undefined);
  const pumpingUnit = parsePumpingUnitFromApi(raw.pumpingUnit as Record<string, unknown> | undefined);
  const linePipe = parseLinePipeFromApi(raw.linePipe as Record<string, unknown> | undefined);
  const storageTank = parseStorageTankFromApi(raw.storageTank as Record<string, unknown> | undefined);
  const pressureVessel = parsePressureVesselFromApi(raw.pressureVessel as Record<string, unknown> | undefined);
  const knockOut = parseKnockOutFromApi(raw.knockOut as Record<string, unknown> | undefined);
  const espCable = parseEspCableFromApi(raw.espCable as Record<string, unknown> | undefined);
  const flare = parseFlareFromApi(raw.flare as Record<string, unknown> | undefined);
  const combustor = parseCombustorFromApi(raw.combustor as Record<string, unknown> | undefined);
  const insRaw = raw.inspectionTypesRequired;
  const inspectionTypesRequired: InspectionTypeCode[] = Array.isArray(insRaw)
    ? (insRaw as string[]).map((x) => parseInspectionTypeCode(String(x))).filter((x): x is InspectionTypeCode => x !== null)
    : [];

  return {
    itemId: String(raw.itemId ?? ''),
    kind,
    stringDesignSubType,
    casing: usesCasingMaterialForm(kind) ? casing ?? emptyCasing() : undefined,
    rods: kind === 'Rods' ? rods ?? emptyRods() : undefined,
    transformer: kind === 'Transformers' ? transformer ?? emptyTransformer() : undefined,
    electricMotor: kind === 'ElectricMotors' ? electricMotor ?? emptyElectricMotor() : undefined,
    pumpingUnit: kind === 'PumpingUnits' ? pumpingUnit ?? emptyPumpingUnit() : undefined,
    linePipe: kind === 'LinePipe' ? linePipe ?? emptyLinePipe() : undefined,
    storageTank: kind === 'StorageTank' ? storageTank ?? emptyStorageTank() : undefined,
    pressureVessel: kind === 'PressureVessel' ? pressureVessel ?? emptyPressureVessel() : undefined,
    knockOut: kind === 'KnockOut' ? knockOut ?? emptyKnockOut() : undefined,
    espCable: kind === 'EspCables' ? espCable ?? emptyEspCable() : undefined,
    flare: kind === 'Flares' ? flare ?? emptyFlare() : undefined,
    combustor: kind === 'Combustor' ? combustor ?? emptyCombustor() : undefined,
    name: String(raw.name ?? ''),
    description: String(raw.description ?? ''),
    quantity: typeof raw.quantity === 'number' ? raw.quantity : Number(raw.quantity ?? 0),
    unit: String(raw.unit ?? ''),
    specifications:
      raw.specifications && typeof raw.specifications === 'object' && raw.specifications !== null
        ? { ...(raw.specifications as Record<string, string>) }
        : {},
    estimatedUnitCost:
      raw.estimatedUnitCost === undefined || raw.estimatedUnitCost === null
        ? undefined
        : Number(raw.estimatedUnitCost),
    totalEstimatedCost:
      raw.totalEstimatedCost === undefined || raw.totalEstimatedCost === null
        ? undefined
        : Number(raw.totalEstimatedCost),
    condition: parseMaterialCondition(raw.condition) ?? 'New',
    bandIfUsed: String(raw.bandIfUsed ?? ''),
    tested: raw.tested === undefined || raw.tested === null ? false : Boolean(raw.tested),
    pressureNumber: String(raw.pressureNumber ?? ''),
    inspectionTypesRequired,
    fullTruckload:
      raw.fullTruckload === undefined || raw.fullTruckload === null ? false : Boolean(raw.fullTruckload),
    sku: String(raw.sku ?? ''),
    skuIsCustom: raw.skuIsCustom === true,
    inventoryFulfilledQuantity: Number(raw.inventoryFulfilledQuantity ?? 0),
    inventoryFulfilledFootage: Number(raw.inventoryFulfilledFootage ?? 0)
  };
}

/** Map one material line from API JSON (procurement request or RFQ snapshot). */
export function mapMaterialItemFromApi(raw: Record<string, unknown>): MaterialItem {
  return normalizeMaterialLine(raw);
}

function normalizeMaterialsList(raw: unknown): MaterialItem[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((x) => normalizeMaterialLine(x as Record<string, unknown>));
}

function normalizeRegionFieldInfo(raw: unknown): RegionFieldInfo {
  if (!raw || typeof raw !== 'object') {
    return emptyRegionFieldInfo();
  }
  const o = raw as Record<string, unknown>;
  const milesRaw = o.milesFromCity;
  return {
    id: String(o.id ?? ''),
    name: String(o.name ?? ''),
    county: String(o.county ?? ''),
    state: String(o.state ?? ''),
    closestCity: String(o.closestCity ?? ''),
    milesFromCity:
      milesRaw === undefined || milesRaw === null || milesRaw === ''
        ? undefined
        : Number(milesRaw)
  };
}

/** Normalizes API JSON (camelCase) from GET /api/requests. */
export function normalizeProcurementRequest(raw: Record<string, unknown>): ProcurementRequest {
  const wellInfo = raw.wellInfo as WellInfo | undefined;
  const requestor = raw.requestor as RequestorProfile | undefined;
  const wi = wellInfo ?? emptyWellInfo();
  const deliveryRaw = raw.deliveryAddressId;

  return {
    id: String(raw.id ?? ''),
    requestId: String(raw.requestId ?? ''),
    requestType: (raw.requestType as string) ?? 'Material',
    requestor: requestor ?? { userId: '', name: '', role: '', region: '' },
    wellInfo: {
      ...emptyWellInfo(),
      ...wi,
      location: { ...emptyWellLocation(), ...wi.location },
      region: typeof wi.region === 'string' ? wi.region : ''
    },
    afeNumber: String(raw.afeNumber ?? ''),
    regionFieldInfo: normalizeRegionFieldInfo(raw.regionFieldInfo),
    deliveryAddressId:
      deliveryRaw !== undefined && deliveryRaw !== null && String(deliveryRaw).trim() !== ''
        ? String(deliveryRaw)
        : undefined,
    businessJustification: (raw.businessJustification as string) ?? '',
    materials: normalizeMaterialsList(raw.materials),
    timeline: raw.timeline as RequestTimeline | undefined,
    vendorPreferences: raw.vendorPreferences as VendorPreferencesSection | undefined,
    compliance: raw.compliance as ComplianceSection | undefined,
    attachments: (raw.attachments as RequestAttachment[]) ?? [],
    audit: (raw.audit as RequestAudit) ?? emptyAudit(),
    status: raw.status as RequestStatus,
    regionalManagerName: raw.regionalManagerName as string | undefined,
    regionalDecisionComment: raw.regionalDecisionComment as string | undefined,
    procurementManagerName: raw.procurementManagerName as string | undefined,
    orderPlacedAtUtc: raw.orderPlacedAtUtc as string | undefined,
    procuredAtUtc: raw.procuredAtUtc as string | undefined,
    fulfilledByMaterialTransferId: raw.fulfilledByMaterialTransferId as string | undefined,
    fulfilledByMaterialTransferNumber: raw.fulfilledByMaterialTransferNumber as string | undefined,
    sourcingInitiatedByMaterialTransferId: raw.sourcingInitiatedByMaterialTransferId as string | undefined,
    createdAtUtc: (raw.createdAtUtc as string) ?? new Date().toISOString(),
    updatedAtUtc: raw.updatedAtUtc as string | undefined,
    collaborationNotes: normalizeCollaborationNotesList(raw.collaborationNotes),
    activityLog: normalizeActivityLog(raw.activityLog)
  };
}

export function requestToFormState(req: ProcurementRequest): ProcurementRequestFormState {
  const wellRegion = (req.wellInfo?.region ?? '').trim();
  const requestorRegion = (req.requestor?.region ?? '').trim();
  return {
    requestType: 'Material',
    requestor: { ...req.requestor, region: wellRegion || requestorRegion },
    wellInfo: {
      ...emptyWellInfo(),
      ...req.wellInfo,
      location: { ...emptyWellLocation(), ...req.wellInfo.location },
      region: wellRegion || requestorRegion
    },
    afeNumber: req.afeNumber ?? '',
    regionFieldInfo: { ...emptyRegionFieldInfo(), ...req.regionFieldInfo },
    deliveryAddressId: req.deliveryAddressId ?? '',
    businessJustification: req.businessJustification,
    materials:
      req.materials.length > 0
        ? req.materials.map((m) => ({
            ...m,
            kind: m.kind ?? 'Tubular',
            specifications: { ...m.specifications },
            inspectionTypesRequired: [...(m.inspectionTypesRequired ?? [])],
            casing: usesCasingMaterialForm(m.kind)
              ? (() => {
                  const cas = { ...emptyCasing(), ...m.casing };
                  hydrateCasingInchUiFromStored(cas);
                  return cas;
                })()
              : undefined,
            rods: m.kind === 'Rods' ? { ...emptyRods(), ...m.rods } : undefined,
            transformer: m.kind === 'Transformers' ? { ...emptyTransformer(), ...m.transformer } : undefined,
            electricMotor: m.kind === 'ElectricMotors' ? { ...emptyElectricMotor(), ...m.electricMotor } : undefined,
            pumpingUnit: m.kind === 'PumpingUnits' ? { ...emptyPumpingUnit(), ...m.pumpingUnit, beam: { ...emptyPumpingUnitBeam(), ...m.pumpingUnit?.beam }, hydraulic: { ...emptyPumpingUnitHydraulic(), ...m.pumpingUnit?.hydraulic } } : undefined,
            linePipe: m.kind === 'LinePipe' ? { ...emptyLinePipe(), ...m.linePipe } : undefined,
            storageTank: m.kind === 'StorageTank' ? { ...emptyStorageTank(), ...m.storageTank } : undefined,
            pressureVessel: m.kind === 'PressureVessel' ? { ...emptyPressureVessel(), ...m.pressureVessel } : undefined,
            knockOut: m.kind === 'KnockOut' ? { ...emptyKnockOut(), ...m.knockOut } : undefined,
            espCable: m.kind === 'EspCables' ? { ...emptyEspCable(), ...m.espCable } : undefined,
            flare: m.kind === 'Flares' ? { ...emptyFlare(), ...m.flare } : undefined,
            combustor: m.kind === 'Combustor' ? { ...emptyCombustor(), ...m.combustor } : undefined
          }))
        : [emptyMaterial()],
    timeline: { ...req.timeline },
    vendorPreferences: {
      preferredVendors: req.vendorPreferences?.preferredVendors?.map((v) => ({ ...v })) ?? [],
      allowAlternateVendors: req.vendorPreferences?.allowAlternateVendors ?? true
    },
    compliance: {
      safetyStandards: [...(req.compliance?.safetyStandards ?? [])],
      environmentalRegulations: [...(req.compliance?.environmentalRegulations ?? [])],
    },
    attachments: req.attachments?.map((a) => ({ ...a })) ?? []
  };
}

export function buildCreateApiPayload(form: ProcurementRequestFormState, saveAsDraft: boolean): Record<string, unknown> {
  return {
    requestType: 'Material',
    requestor: {
      userId: form.requestor.userId || null,
      name: form.requestor.name || null,
      role: form.requestor.role || null,
      region: form.requestor.region || null
    },
    wellInfo: {
      wellId: form.wellInfo.wellId || null,
      wellName: form.wellInfo.wellName || null,
      field: form.wellInfo.field || null,
      region: form.wellInfo.region || null,
      location: {
        state: form.wellInfo.location.state || null,
        country: form.wellInfo.location.country || null
      },
      depthFt: form.wellInfo.depthFt ?? null,
      reservoirType: form.wellInfo.reservoirType || null
    },
    afeNumber: form.afeNumber.trim() || null,
    regionFieldInfo: {
      id: form.regionFieldInfo.id.trim() || null,
      name: form.regionFieldInfo.name.trim() || null,
      county: form.regionFieldInfo.county.trim() || null,
      state: form.regionFieldInfo.state.trim() || null,
      closestCity: form.regionFieldInfo.closestCity.trim() || null,
      milesFromCity:
        form.regionFieldInfo.milesFromCity === undefined || form.regionFieldInfo.milesFromCity === null
          ? null
          : form.regionFieldInfo.milesFromCity
    },
    deliveryAddressId: form.deliveryAddressId.trim() || null,
    businessJustification: form.businessJustification,
    materials: form.materials.map((m) => ({
      itemId: m.itemId || null,
      kind: m.kind,
      stringDesignSubType: m.kind === 'StringDesign' ? m.stringDesignSubType ?? null : null,
      casing:
        usesCasingMaterialForm(m.kind) && m.casing
          ? (() => {
              const c = m.casing;
              const makeupFromParts = inchesPartsToDecimal(c.plusMakeupInchesWhole, c.plusMakeupInchesFraction);
              const makeup =
                makeupFromParts !== undefined
                  ? makeupFromParts
                  : c.plusMakeupThreadLoss !== undefined && !Number.isNaN(Number(c.plusMakeupThreadLoss))
                    ? Number(c.plusMakeupThreadLoss)
                    : null;
              const sizeCombined =
                formatInchesStored(c.sizeInchesWhole, c.sizeInchesFraction).trim() || c.sizeDiameter?.trim() || null;
              return {
                netFootage: c.netFootage ?? null,
                plusMakeupThreadLoss: makeup,
                grossFootage: c.grossFootage ?? null,
                sizeDiameter: sizeCombined,
                weight: c.weight || null,
                grade: c.grade || null,
                thread: c.thread || null,
                erwOrSeamless: c.mfgProcess || null
              };
            })()
          : null,
      rods:
        m.kind === 'Rods' && m.rods
          ? {
              materialFamily: m.rods.materialFamily,
              steelGrade: m.rods.materialFamily === 'Steel' ? m.rods.steelGrade ?? null : null,
              rodDiameterInchesWhole: m.rods.rodDiameterInchesWhole || null,
              rodDiameterInchesFraction: m.rods.rodDiameterInchesFraction || null,
              lengthFeet: m.rods.lengthFeet ?? null,
              ponyRodsLengthInches: m.rods.ponyRodsLengthInches ?? null,
              endConnectionsQuantity: m.rods.endConnectionsQuantity ?? null,
              rodGuidesQuantity: m.rods.rodGuidesQuantity ?? null,
              polishedRodLengthFeet: m.rods.polishedRodLengthFeet ?? null,
              polishedRodInchesWhole: m.rods.polishedRodInchesWhole || null,
              polishedRodInchesFraction: m.rods.polishedRodInchesFraction || null
            }
          : null,
      transformer:
        m.kind === 'Transformers' && m.transformer
          ? {
              voltageRating: m.transformer.voltageRating || null,
              capacity: m.transformer.capacity || null,
              cooling: m.transformer.cooling ?? null,
              insulationClass: m.transformer.insulationClass || null,
              impedancePercent: m.transformer.impedancePercent ?? null,
              environmentalProtection: m.transformer.environmentalProtection || null,
              oilType: m.transformer.oilType || null
            }
          : null,
      electricMotor:
        m.kind === 'ElectricMotors' && m.electricMotor
          ? {
              powerOutputHp: m.electricMotor.powerOutputHp ?? null,
              torqueDesign: m.electricMotor.torqueDesign || null,
              speedRpm: m.electricMotor.speedRpm ?? null,
              voltage: m.electricMotor.voltage ?? null,
              enclosure: m.electricMotor.enclosure ?? null,
              duty: m.electricMotor.duty || null
            }
          : null,
      pumpingUnit:
        m.kind === 'PumpingUnits' && m.pumpingUnit
          ? {
              unitType: m.pumpingUnit.unitType,
              beam: m.pumpingUnit.beam
                ? {
                    apiSpecifications: m.pumpingUnit.beam.apiSpecifications || null,
                    loadRatingLbs: m.pumpingUnit.beam.loadRatingLbs ?? null,
                    strokeLengthFeet: m.pumpingUnit.beam.strokeLengthFeet ?? null,
                    strokeInchesWhole: m.pumpingUnit.beam.strokeInchesWhole || null,
                    strokeInchesFraction: m.pumpingUnit.beam.strokeInchesFraction || null,
                    counterbalance: m.pumpingUnit.beam.counterbalance ?? null,
                    primeMover: m.pumpingUnit.beam.primeMover ?? null,
                    operatingStrokesPerMinute: m.pumpingUnit.beam.operatingStrokesPerMinute ?? null
                  }
                : null,
              hydraulic: m.pumpingUnit.hydraulic
                ? {
                    strokeFeet: m.pumpingUnit.hydraulic.strokeFeet ?? null,
                    strokeInchesWhole: m.pumpingUnit.hydraulic.strokeInchesWhole || null,
                    strokeInchesFraction: m.pumpingUnit.hydraulic.strokeInchesFraction || null,
                    peakLoadLbm: m.pumpingUnit.hydraulic.peakLoadLbm ?? null
                  }
                : null
            }
          : null,
      linePipe:
        m.kind === 'LinePipe' && m.linePipe
          ? {
              diameter: m.linePipe.diameter || null,
              wallThickness: m.linePipe.wallThickness || null,
              lengthFeet: m.linePipe.lengthFeet ?? null,
              lengthInchesWhole: m.linePipe.lengthInchesWhole || null,
              lengthInchesFraction: m.linePipe.lengthInchesFraction || null,
              endFinishes: m.linePipe.endFinishes || null,
              manufacturingProcess: m.linePipe.manufacturingProcess || null,
              grade: m.linePipe.grade || null
            }
          : null,
      storageTank:
        m.kind === 'StorageTank' && m.storageTank
          ? {
              standard: m.storageTank.standard ?? null,
              capacityBbl: m.storageTank.capacityBbl ?? null,
              operatingPressurePsig: m.storageTank.operatingPressurePsig ?? null,
              materials: m.storageTank.materials || null
            }
          : null,
      pressureVessel:
        m.kind === 'PressureVessel' && m.pressureVessel
          ? {
              standards: m.pressureVessel.standards || null,
              pressureRatingPsig: m.pressureVessel.pressureRatingPsig ?? null
            }
          : null,
      knockOut:
        m.kind === 'KnockOut' && m.knockOut
          ? {
              drumType: m.knockOut.drumType ?? null,
              retentionTimeMinutes: m.knockOut.retentionTimeMinutes ?? null,
              sizes: m.knockOut.sizes || null
            }
          : null,
      espCable:
        m.kind === 'EspCables' && m.espCable
          ? {
              voltageKv: m.espCable.voltageKv ?? null,
              temperatureFahrenheit: m.espCable.temperatureFahrenheit ?? null,
              conductorSizes: m.espCable.conductorSizes || null,
              cableGeometry: m.espCable.cableGeometry || null,
              conductors: m.espCable.conductors || null,
              insulation: m.espCable.insulation || null,
              barrierOrSheath: m.espCable.barrierOrSheath || null,
              jacket: m.espCable.jacket || null,
              armor: m.espCable.armor || null
            }
          : null,
      flare:
        m.kind === 'Flares' && m.flare
          ? {
              standards: m.flare.standards || null,
              destructionEfficiencyPercent: m.flare.destructionEfficiencyPercent ?? null,
              netHeatingValueBtuScf: m.flare.netHeatingValueBtuScf ?? null,
              tipVelocityFps: m.flare.tipVelocityFps ?? null,
              visibleEmissions: m.flare.visibleEmissions || null,
              pilot: m.flare.pilot === undefined ? null : m.flare.pilot
            }
          : null,
      combustor:
        m.kind === 'Combustor' && m.combustor
          ? {
              performancePercent: m.combustor.performancePercent ?? null,
              flowLimits: m.combustor.flowLimits || null
            }
          : null,
      name: m.kind === 'Tubular' && !m.name.trim() ? 'Tubular' : m.name,
      description: m.description || null,
      quantity: m.quantity,
      unit: m.unit?.trim() || 'EA',
      specifications: Object.keys(m.specifications).length > 0 ? m.specifications : null,
      estimatedUnitCost: m.estimatedUnitCost ?? null,
      totalEstimatedCost: m.totalEstimatedCost ?? null,
      condition: m.condition ?? null,
      bandIfUsed: m.bandIfUsed?.trim() || null,
      tested: m.tested === undefined ? null : m.tested,
      pressureNumber: m.pressureNumber?.trim() || null,
      inspectionTypesRequired:
        (m.inspectionTypesRequired?.length ?? 0) > 0 ? m.inspectionTypesRequired : null,
      fullTruckload: m.fullTruckload === undefined ? null : m.fullTruckload,
      sku: m.sku?.trim() || null,
      skuIsCustom: m.skuIsCustom === true ? true : null
    })),
    timeline:
      form.timeline.requiredByDate || form.timeline.expectedDeliveryDate
        ? {
            requiredByDate: form.timeline.requiredByDate || null,
            expectedDeliveryDate: form.timeline.expectedDeliveryDate || null
          }
        : null,
    vendorPreferences:
      form.vendorPreferences.preferredVendors.length > 0 || !form.vendorPreferences.allowAlternateVendors
        ? {
            preferredVendors: form.vendorPreferences.preferredVendors.map((v) => ({
              vendorId: v.vendorId || null,
              name: v.name || null
            })),
            allowAlternateVendors: form.vendorPreferences.allowAlternateVendors
          }
        : null,
    compliance:
      form.compliance.safetyStandards.length > 0 || form.compliance.environmentalRegulations.length > 0
        ? {
            safetyStandards: form.compliance.safetyStandards.length > 0 ? form.compliance.safetyStandards : null,
            environmentalRegulations:
              form.compliance.environmentalRegulations.length > 0 ? form.compliance.environmentalRegulations : null
          }
        : null,
    attachments: form.attachments.length > 0 ? form.attachments : null,
    saveAsDraft
  };
}

export function buildUpdateDraftApiPayload(form: ProcurementRequestFormState): Record<string, unknown> {
  const p = { ...buildCreateApiPayload(form, false) } as Record<string, unknown>;
  delete p.saveAsDraft;
  return p;
}

export function requestDisplayTitle(req: ProcurementRequest): string {
  return req.wellInfo?.wellName?.trim() || req.requestId || '—';
}

const KIND_SUMMARY: Partial<Record<MaterialLineKind, string>> = {
  Tubing: 'Tubing',
  Casing: 'Casing',
  Rods: 'Rods',
  Transformers: 'Transformers',
  ElectricMotors: 'Electric motors',
  PumpingUnits: 'Pumping units',
  LinePipe: 'Line pipe',
  TanksPressureVesselsKnockOuts: 'Tanks / vessels / KOs',
  EspCables: 'ESP cables',
  FlaresCombustors: 'Flares / combustors',
  StorageTank: 'Storage tank',
  PressureVessel: 'Pressure vessel',
  KnockOut: 'Knock out',
  Flares: 'Flare',
  Combustor: 'Combustor'
};

function tx(s: string | undefined): boolean {
  return (s ?? '').trim() !== '';
}

function polishedRodLengthOk(r: RodLineDetails): boolean {
  if (r.polishedRodLengthFeet != null && !Number.isNaN(r.polishedRodLengthFeet) && r.polishedRodLengthFeet > 0) {
    return true;
  }
  return inchesPartsFilled(r.polishedRodInchesWhole, r.polishedRodInchesFraction);
}

export function structuredEquipmentLineComplete(m: MaterialItem): boolean {
  switch (m.kind) {
    case 'Transformers': {
      const t = m.transformer;
      if (!t) {
        return false;
      }
      return (
        tx(t.voltageRating) &&
        tx(t.capacity) &&
        t.cooling != null &&
        tx(t.insulationClass) &&
        t.impedancePercent != null &&
        !Number.isNaN(t.impedancePercent) &&
        t.impedancePercent >= 0 &&
        tx(t.environmentalProtection) &&
        tx(t.oilType)
      );
    }
    case 'ElectricMotors': {
      const e = m.electricMotor;
      if (!e) {
        return false;
      }
      return (
        e.powerOutputHp != null &&
        !Number.isNaN(e.powerOutputHp) &&
        e.powerOutputHp > 0 &&
        tx(e.torqueDesign) &&
        e.speedRpm != null &&
        !Number.isNaN(e.speedRpm) &&
        e.speedRpm > 0 &&
        e.voltage != null &&
        !Number.isNaN(e.voltage) &&
        e.voltage > 0 &&
        e.enclosure != null &&
        tx(e.duty)
      );
    }
    case 'PumpingUnits': {
      const p = m.pumpingUnit;
      if (!p) {
        return false;
      }
      if (p.unitType === 'BeamPumping') {
        const b = p.beam;
        if (!b) {
          return false;
        }
        const hasStroke =
          (b.strokeLengthFeet != null && !Number.isNaN(b.strokeLengthFeet) && b.strokeLengthFeet > 0) ||
          inchesPartsFilled(b.strokeInchesWhole, b.strokeInchesFraction);
        return (
          tx(b.apiSpecifications) &&
          b.loadRatingLbs != null &&
          !Number.isNaN(b.loadRatingLbs) &&
          b.loadRatingLbs > 0 &&
          hasStroke &&
          b.counterbalance != null &&
          b.primeMover != null &&
          b.operatingStrokesPerMinute != null &&
          !Number.isNaN(b.operatingStrokesPerMinute) &&
          b.operatingStrokesPerMinute > 0
        );
      }
      const h = p.hydraulic;
      if (!h) {
        return false;
      }
      const hasStrokeH =
        (h.strokeFeet != null && !Number.isNaN(h.strokeFeet) && h.strokeFeet > 0) ||
        inchesPartsFilled(h.strokeInchesWhole, h.strokeInchesFraction);
      return hasStrokeH && h.peakLoadLbm != null && !Number.isNaN(h.peakLoadLbm) && h.peakLoadLbm > 0;
    }
    case 'LinePipe': {
      const l = m.linePipe;
      if (!l) {
        return false;
      }
      const hasLen =
        (l.lengthFeet != null && !Number.isNaN(l.lengthFeet) && l.lengthFeet > 0) ||
        inchesPartsFilled(l.lengthInchesWhole, l.lengthInchesFraction);
      return (
        tx(l.diameter) &&
        tx(l.wallThickness) &&
        hasLen &&
        tx(l.endFinishes) &&
        tx(l.manufacturingProcess) &&
        tx(l.grade)
      );
    }
    case 'StorageTank': {
      const s = m.storageTank;
      if (!s) {
        return false;
      }
      return (
        s.standard != null &&
        s.capacityBbl != null &&
        !Number.isNaN(s.capacityBbl) &&
        s.capacityBbl > 0 &&
        s.operatingPressurePsig != null &&
        !Number.isNaN(s.operatingPressurePsig) &&
        s.operatingPressurePsig >= 0 &&
        tx(s.materials)
      );
    }
    case 'PressureVessel': {
      const p = m.pressureVessel;
      if (!p) {
        return false;
      }
      return tx(p.standards) && p.pressureRatingPsig != null && !Number.isNaN(p.pressureRatingPsig) && p.pressureRatingPsig > 0;
    }
    case 'KnockOut': {
      const k = m.knockOut;
      if (!k) {
        return false;
      }
      return (
        k.drumType != null &&
        k.retentionTimeMinutes != null &&
        !Number.isNaN(k.retentionTimeMinutes) &&
        k.retentionTimeMinutes > 0 &&
        tx(k.sizes)
      );
    }
    case 'EspCables': {
      const e = m.espCable;
      if (!e) {
        return false;
      }
      return (
        e.voltageKv != null &&
        !Number.isNaN(e.voltageKv) &&
        e.voltageKv > 0 &&
        e.temperatureFahrenheit != null &&
        !Number.isNaN(e.temperatureFahrenheit) &&
        tx(e.conductorSizes) &&
        tx(e.cableGeometry) &&
        tx(e.conductors) &&
        tx(e.insulation) &&
        tx(e.barrierOrSheath) &&
        tx(e.jacket) &&
        tx(e.armor)
      );
    }
    case 'Flares': {
      const f = m.flare;
      if (!f) {
        return false;
      }
      return (
        tx(f.standards) &&
        f.destructionEfficiencyPercent != null &&
        !Number.isNaN(f.destructionEfficiencyPercent) &&
        f.destructionEfficiencyPercent >= 0 &&
        f.destructionEfficiencyPercent <= 100 &&
        f.netHeatingValueBtuScf != null &&
        !Number.isNaN(f.netHeatingValueBtuScf) &&
        f.netHeatingValueBtuScf > 0 &&
        f.tipVelocityFps != null &&
        !Number.isNaN(f.tipVelocityFps) &&
        f.tipVelocityFps > 0 &&
        tx(f.visibleEmissions) &&
        f.pilot !== undefined
      );
    }
    case 'Combustor': {
      const c = m.combustor;
      if (!c) {
        return false;
      }
      return (
        c.performancePercent != null &&
        !Number.isNaN(c.performancePercent) &&
        c.performancePercent >= 0 &&
        c.performancePercent <= 100 &&
        tx(c.flowLimits)
      );
    }
    default:
      return false;
  }
}

export function materialsSummary(req: ProcurementRequest): string {
  if (req.materials.length === 0) {
    return '—';
  }
  const m = req.materials[0];
  let first: string;
  if (m.kind === 'StringDesign') {
    first =
      (m.stringDesignSubType && STRING_DESIGN_SUBTYPE_LABELS[m.stringDesignSubType]) || 'String design';
  } else if (usesCasingMaterialForm(m.kind) && m.casing?.sizeDiameter?.trim()) {
    first = `${KIND_SUMMARY[m.kind] ?? m.kind} ${m.casing.sizeDiameter.trim()}`;
  } else if (m.name?.trim()) {
    const label = KIND_SUMMARY[m.kind] ?? m.kind;
    first = `${label}: ${m.name.trim()}`;
  } else {
    first = (KIND_SUMMARY[m.kind] as string) ?? m.kind;
  }
  const more = req.materials.length - 1;
  return more > 0 ? `${first} (+${more})` : first;
}

export function totalMaterialQuantity(req: ProcurementRequest): number {
  return req.materials.reduce((s, m) => s + (m.quantity > 0 ? m.quantity : 0), 0);
}

export function primaryMaterialLine(req: ProcurementRequest): MaterialItem | undefined {
  return req.materials[0];
}

export function validateDraftSave(form: ProcurementRequestFormState): string | null {
  if (!form.wellInfo.wellName.trim() && !form.businessJustification.trim()) {
    return 'Add a well name or business justification before saving a draft.';
  }
  return null;
}

/** Ensures the request references a well that exists in the directory (normalized keys). */
export function validateWellSelectedFromDirectory(
  form: ProcurementRequestFormState,
  directoryIds: Set<string>
): string | null {
  const id = form.wellInfo.wellId.trim().toLowerCase();
  if (!id) {
    return 'Select a well from the directory.';
  }
  const known = new Set([...directoryIds].map((x) => x.trim().toLowerCase()));
  if (!known.has(id)) {
    return 'Choose a well from the directory.';
  }
  return null;
}

function casingComplete(c: CasingLineDetails): boolean {
  const makeupOk = inchesPartsFilled(c.plusMakeupInchesWhole, c.plusMakeupInchesFraction);
  const sizeOk = inchesPartsFilled(c.sizeInchesWhole, c.sizeInchesFraction);
  const weightOk = c.weight.trim() !== '' && !Number.isNaN(Number(c.weight));
  return (
    c.netFootage !== undefined &&
    !Number.isNaN(c.netFootage) &&
    c.grossFootage !== undefined &&
    !Number.isNaN(c.grossFootage) &&
    makeupOk &&
    sizeOk &&
    weightOk &&
    !!c.grade.trim() &&
    !!c.thread.trim() &&
    (c.mfgProcess === 'Erw' || c.mfgProcess === 'Seamless')
  );
}

/** Field keys for highlighting invalid controls after a failed submit-for-approval attempt. */
export function getSubmitValidationFieldErrors(form: ProcurementRequestFormState): string[] {
  const out: string[] = [];
  const add = (k: string): void => {
    if (!out.includes(k)) {
      out.push(k);
    }
  };

  if (!form.wellInfo.wellId.trim() || !form.wellInfo.wellName.trim()) {
    add('well');
  }
  if (!form.wellInfo.region.trim()) {
    add('region');
  }
  if (!form.afeNumber.trim()) {
    add('afeNumber');
  }
  if (!form.regionFieldInfo.id.trim()) {
    add('requestField');
  }
  if (!form.timeline.requiredByDate?.trim()) {
    add('dateRequired');
  }
  if (!form.deliveryAddressId.trim()) {
    add('deliveryAddress');
  }
  if (!form.businessJustification.trim()) {
    add('businessJustification');
  }
  if (form.materials.length === 0) {
    add('materials');
  }

  form.materials.forEach((m, i) => {
    if (usesCasingMaterialForm(m.kind)) {
      if (m.kind === 'StringDesign' && !m.stringDesignSubType) {
        add(`material-${i}-stringType`);
      }
      if (!m.casing || !casingComplete(m.casing)) {
        add(`material-${i}-casing`);
      }
      if (m.quantity <= 0) {
        add(`material-${i}-quantity`);
      }
    } else if (m.kind === 'Rods') {
      const r = m.rods;
      if (!r) {
        add(`material-${i}-rods`);
      } else {
        if (r.materialFamily === 'Steel' && !r.steelGrade) {
          add(`material-${i}-rods`);
        }
        if (!inchesPartsFilled(r.rodDiameterInchesWhole, r.rodDiameterInchesFraction)) {
          add(`material-${i}-rods`);
        }
        if (r.lengthFeet === undefined || Number.isNaN(r.lengthFeet) || r.lengthFeet <= 0) {
          add(`material-${i}-rods`);
        }
        if (
          r.endConnectionsQuantity === undefined ||
          Number.isNaN(r.endConnectionsQuantity) ||
          r.endConnectionsQuantity < 0 ||
          r.rodGuidesQuantity === undefined ||
          Number.isNaN(r.rodGuidesQuantity) ||
          r.rodGuidesQuantity < 0 ||
          !polishedRodLengthOk(r)
        ) {
          add(`material-${i}-rods`);
        }
      }
      if (m.quantity <= 0) {
        add(`material-${i}-quantity`);
      }
    } else if (usesStructuredEquipmentForm(m.kind)) {
      if (!structuredEquipmentLineComplete(m)) {
        add(`material-${i}-equipment`);
      }
      if (m.quantity <= 0) {
        add(`material-${i}-quantity`);
      }
    } else if (isGenericMaterialKindUi(m.kind)) {
      if (!m.name?.trim()) {
        add(`material-${i}-name`);
      }
      if (!m.unit?.trim()) {
        add(`material-${i}-unit`);
      }
      if (m.quantity <= 0) {
        add(`material-${i}-quantity`);
      }
    } else {
      add(`material-${i}-kind`);
    }
    if (!skipsMaterialLogisticsSection(m.kind)) {
      if (!m.condition) {
        add(`material-${i}-condition`);
      }
      if (m.condition === 'Used' && !m.bandIfUsed?.trim()) {
        add(`material-${i}-band`);
      }
      if (m.tested === undefined) {
        add(`material-${i}-tested`);
      }
      if (m.tested === true && !m.pressureNumber?.trim()) {
        add(`material-${i}-pressure`);
      }
      if ((m.inspectionTypesRequired ?? []).length === 0) {
        add(`material-${i}-inspection`);
      }
      if (m.fullTruckload === undefined) {
        add(`material-${i}-truckload`);
      }
    }
  });

  return out;
}

export const SUBMIT_FIELD_WELL_DIRECTORY = 'wellDirectory';

export function validateSubmitForm(form: ProcurementRequestFormState): string | null {
  if (!form.wellInfo.wellId.trim() || !form.wellInfo.wellName.trim()) {
    return 'Complete well id and well name.';
  }
  if (!form.wellInfo.region.trim()) {
    return 'Well region is required. Select a well that has a region.';
  }
  if (!form.afeNumber.trim()) {
    return 'AFE# is required.';
  }
  if (!form.regionFieldInfo.id.trim()) {
    return 'Field is required (choose from the list for this region).';
  }
  if (!form.timeline.requiredByDate?.trim()) {
    return 'Date required is required.';
  }
  if (!form.deliveryAddressId.trim()) {
    return 'Delivery address is required.';
  }
  if (!form.businessJustification.trim()) {
    return 'Business justification is required.';
  }
  if (form.materials.length === 0) {
    return 'Add at least one material line.';
  }
  for (const m of form.materials) {
    if (usesCasingMaterialForm(m.kind)) {
      if (m.kind === 'StringDesign' && !m.stringDesignSubType) {
        return 'Each string design line must select a casing type.';
      }
      if (!m.casing || !casingComplete(m.casing)) {
        return 'Each tubular / tubing / casing line must include all casing details (footage, size, weight, grade, thread, Mfg. process).';
      }
      if (m.quantity <= 0) {
        return 'Each line needs a positive quantity.';
      }
    } else if (m.kind === 'Rods') {
      const r = m.rods;
      if (!r) {
        return 'Rods line is missing details.';
      }
      if (r.materialFamily === 'Steel' && !r.steelGrade) {
        return 'Steel rods must include an API grade (C, D, or K).';
      }
      if (r.materialFamily === 'Fiberglass' && r.steelGrade) {
        return 'Fiberglass rods must not set a steel API grade.';
      }
      if (!inchesPartsFilled(r.rodDiameterInchesWhole, r.rodDiameterInchesFraction)) {
        return 'Rods: enter diameter (whole inches and/or fraction).';
      }
      if (r.lengthFeet === undefined || Number.isNaN(r.lengthFeet) || r.lengthFeet <= 0) {
        return 'Rods: length in feet is required and must be positive.';
      }
      if (r.endConnectionsQuantity === undefined || Number.isNaN(r.endConnectionsQuantity) || r.endConnectionsQuantity < 0) {
        return 'Rods: end connections (quantity) is required (0 or more).';
      }
      if (r.rodGuidesQuantity === undefined || Number.isNaN(r.rodGuidesQuantity) || r.rodGuidesQuantity < 0) {
        return 'Rods: rod guides (quantity) is required (0 or more).';
      }
      if (!polishedRodLengthOk(r)) {
        return 'Rods: polished rod length (feet and/or inches) is required.';
      }
      if (m.quantity <= 0) {
        return 'Rods line needs a positive quantity.';
      }
    } else if (usesStructuredEquipmentForm(m.kind)) {
      if (!structuredEquipmentLineComplete(m)) {
        return 'Complete the equipment specification block for this material line (all required fields).';
      }
      if (m.quantity <= 0) {
        return 'Each equipment line needs a positive quantity.';
      }
    } else if (isGenericMaterialKindUi(m.kind)) {
      if (!m.name.trim()) {
        return 'Each equipment line must include a short name or title.';
      }
      if (m.quantity <= 0) {
        return 'Each equipment line needs a positive quantity.';
      }
      if (!m.unit?.trim()) {
        return 'Each equipment line must include a unit of measure (e.g. EA).';
      }
    } else {
      return 'Unsupported material type in this form.';
    }
    if (!skipsMaterialLogisticsSection(m.kind)) {
      const log = validateMaterialLogisticsLine(m);
      if (log) {
        return log;
      }
    }
  }
  return null;
}

function validateMaterialLogisticsLine(m: MaterialItem): string | null {
  if (skipsMaterialLogisticsSection(m.kind)) {
    return null;
  }
  if (!m.condition) {
    return 'Each material line must specify NEW or USED.';
  }
  if (m.condition === 'Used' && !m.bandIfUsed?.trim()) {
    return 'BAND is required when material is USED.';
  }
  if (m.tested === undefined) {
    return 'Each material line must specify whether material was TESTED (Yes or No).';
  }
  if (m.tested === true && !m.pressureNumber?.trim()) {
    return '# PRESSURE is required when TESTED is Yes.';
  }
  const ins = m.inspectionTypesRequired ?? [];
  if (ins.length === 0) {
    return 'Select at least one TYPE OF INSPECTION REQUIRED option on each material line.';
  }
  if (m.fullTruckload === undefined) {
    return 'Each material line must specify FULL TRUCKLOAD (Yes or No).';
  }
  return null;
}
